/**
 * /admin/subscriptions — list every subscription, filter by status,
 * search by name/email, see MRR at the top, export CSV.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Shield, RefreshCw, LogOut, Loader2, AlertCircle, ChevronLeft,
  Search, BarChart3, DollarSign, Download, Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';

const STATE_META = {
  trialing:  { label: 'Trialing',  pill: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40' },
  active:    { label: 'Active',    pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  past_due:  { label: 'Past due',  pill: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  canceled:  { label: 'Canceled',  pill: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
  paused:    { label: 'Paused',    pill: 'bg-white/10 text-white/65 border-white/15' },
};

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'trialing', label: 'Trialing' },
  { key: 'active',   label: 'Active' },
  { key: 'past_due', label: 'Past due' },
  { key: 'canceled', label: 'Canceled' },
];

const fmtMoney = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso; }
};

const SubscriptionsAdminPage = () => {
  const [adminKey, setAdminKey] = useState(() => { try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; } });
  const [authed, setAuthed] = useState(!!adminKey);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ subscriptions: [], counts: {}, mrr_cents: 0, mrr_count: 0 });
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const fetchData = async (key, status = filter) => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/admin/subscriptions`, {
        headers: { 'X-Admin-Key': key },
        params: status && status !== 'all' ? { status, limit: 500 } : { limit: 500 },
      });
      setData(r.data); setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 403) {
        setError('Invalid admin key.');
        setAuthed(false);
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not load subscriptions.');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (adminKey) fetchData(adminKey, filter); /* eslint-disable-next-line */ }, []);

  const submitKey = (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    try { localStorage.setItem(ADMIN_KEY_LS, k); } catch {}
    setAdminKey(k); fetchData(k, filter);
  };

  const logout = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey(''); setAuthed(false); setData({ subscriptions: [], counts: {}, mrr_cents: 0, mrr_count: 0 });
  };

  const onFilterChange = (k) => { setFilter(k); fetchData(adminKey, k); };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.subscriptions || [];
    return (data.subscriptions || []).filter((s) =>
      [s.restaurant_name, s.owner_name, s.owner_email, s.stripe_customer_id, s.stripe_subscription_id, s.plan].some(
        (f) => (f || '').toLowerCase().includes(q),
      ),
    );
  }, [data.subscriptions, query]);

  const copy = async (t) => { try { await navigator.clipboard.writeText(t); } catch {} };

  const downloadCSV = () => {
    const rows = filtered;
    if (!rows.length) return;
    const headers = ['restaurant_name', 'owner_name', 'owner_email', 'plan', 'status', 'amount_cents', 'trial_ends_at', 'current_period_end', 'stripe_customer_id', 'stripe_subscription_id', 'subscribed_at'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `nofee_subscriptions_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <form onSubmit={submitKey} className="w-full max-w-md p-7 rounded-3xl border border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-yellow-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Admin · Subscriptions</span>
          </div>
          <h1 className="font-['Outfit',_Inter,_sans-serif] text-2xl font-semibold tracking-tight mb-1">Sign in</h1>
          <p className="text-[13px] text-white/55 mb-5">Enter the NoFee admin key.</p>
          <input
            type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            data-testid="subs-admin-key-input"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/12 text-white text-[14px] focus:outline-none focus:border-yellow-500/55"
          />
          {error && <p className="mt-3 text-[12.5px] text-rose-300">{error}</p>}
          <button type="submit" data-testid="subs-admin-key-submit" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px]">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="subs-admin-page">
      <header className="sticky top-0 z-10 bg-black/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link to="/admin" data-testid="subs-back-to-hub" className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] text-white/55 hover:text-white text-[12.5px] font-bold">
              <ChevronLeft className="w-3.5 h-3.5" /> Admin
            </Link>
            <span className="text-white/15">/</span>
            <BarChart3 className="w-5 h-5 text-yellow-300" />
            <h1 className="text-[16px] font-bold tracking-tight">Subscriptions</h1>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fetchData(adminKey, filter)} disabled={loading} data-testid="subs-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-yellow-500/40 text-white/85 text-[12.5px] font-bold disabled:opacity-60">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
            </button>
            <button type="button" onClick={downloadCSV} disabled={!filtered.length} data-testid="subs-export"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-yellow-500/40 text-white/85 text-[12.5px] font-bold disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button type="button" onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-rose-500/40 text-white/65 hover:text-rose-300 text-[12.5px] font-bold">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* MRR hero card */}
        <div data-testid="subs-mrr-card" className="p-5 sm:p-7 mb-5 rounded-3xl border border-yellow-500/35 bg-gradient-to-br from-yellow-500/[0.07] via-zinc-950 to-black">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-yellow-300" />
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Monthly recurring revenue</span>
              </div>
              <div className="font-['Outfit',_Inter,_sans-serif] text-5xl sm:text-6xl font-semibold tracking-[-0.02em] bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent tabular-nums">
                {fmtMoney(data.mrr_cents)}
              </div>
              <p className="mt-1 text-[12.5px] text-white/55"><strong className="text-white tabular-nums">{data.mrr_count}</strong> active + trialing subscriptions</p>
            </div>
            <div className="text-right text-[12px] text-white/45">
              <div>Total {data.counts?.all || 0}</div>
              <div className="text-white/30">Last update {new Date().toLocaleTimeString()}</div>
            </div>
          </div>
        </div>

        {/* Filters + search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex gap-1.5 flex-wrap" data-testid="subs-filters">
            {FILTERS.map((f) => {
              const c = data.counts?.[f.key];
              const active = filter === f.key;
              return (
                <button key={f.key} type="button" onClick={() => onFilterChange(f.key)} data-testid={`subs-filter-${f.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all border ${
                    active
                      ? 'bg-yellow-500/10 border-yellow-500/45 text-yellow-200'
                      : 'border-white/10 bg-white/[0.02] text-white/65 hover:border-yellow-500/30 hover:text-white'
                  }`}>
                  {f.label}
                  {typeof c === 'number' && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] tabular-nums ${active ? 'bg-yellow-500/20' : 'bg-white/[0.05]'}`}>{c}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="sm:ml-auto relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search restaurant, email, plan…" data-testid="subs-search"
              className="w-full sm:w-80 pl-10 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[13px] placeholder-white/35 focus:outline-none focus:border-yellow-500/55" />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-300 text-[13px] flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {loading && filtered.length === 0 ? (
          <div className="text-white/45 text-[14px]" data-testid="subs-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="subs-empty" className="p-8 sm:p-12 rounded-3xl border border-white/10 bg-white/[0.02] text-center">
            <BarChart3 className="w-9 h-9 text-yellow-300/65 mx-auto mb-4" />
            <h2 className="text-[16px] font-bold tracking-tight">No subscriptions in this view</h2>
            <p className="mt-2 text-[13px] text-white/55">Try changing the filter or clearing the search.</p>
          </div>
        ) : (
          <div className="rounded-3xl border border-white/8 bg-white/[0.015] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="subs-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-[0.16em] text-white/55 border-b border-white/[0.06]">
                    <th className="px-4 py-3 font-bold">Restaurant</th>
                    <th className="px-4 py-3 font-bold">Owner</th>
                    <th className="px-4 py-3 font-bold">Plan</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                    <th className="px-4 py-3 font-bold">MRR</th>
                    <th className="px-4 py-3 font-bold">Trial ends</th>
                    <th className="px-4 py-3 font-bold">Started</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const meta = STATE_META[s.status] || STATE_META.active;
                    return (
                      <tr key={s.id || s.stripe_subscription_id} data-testid={`subs-row-${s.stripe_subscription_id || s.id}`}
                        className="border-b border-white/[0.04] hover:bg-yellow-500/[0.025] transition-colors">
                        <td className="px-4 py-3.5">
                          <div className="font-semibold text-white">{s.restaurant_name || '—'}</div>
                          <button onClick={() => copy(s.stripe_subscription_id || '')} className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] text-white/40 hover:text-yellow-300 font-mono">
                            {(s.stripe_subscription_id || '').slice(0, 18)} <Copy className="w-2.5 h-2.5" />
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="text-white/85">{s.owner_name || '—'}</div>
                          <a href={`mailto:${s.owner_email}`} className="text-[11.5px] text-yellow-300/85 hover:text-yellow-200">{s.owner_email || '—'}</a>
                        </td>
                        <td className="px-4 py-3.5 text-white/85">{s.plan || '—'}</td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${meta.pill}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 font-semibold text-white tabular-nums">{fmtMoney(s.amount_cents)}</td>
                        <td className="px-4 py-3.5 text-white/65 tabular-nums">{fmtDate(s.trial_ends_at)}</td>
                        <td className="px-4 py-3.5 text-white/65 tabular-nums">{fmtDate(s.subscribed_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SubscriptionsAdminPage;
